/**
 * PS5 Mount Bundle - JavaScript
 * Handles product switching, bundle selection, and cart functionality
 */

(function() {
  'use strict';

  // Get section ID from data attribute or use default
  const bundleSection = document.querySelector('[data-ps5-bundle-section-id]');
  const sectionId = bundleSection ? bundleSection.dataset.ps5BundleSectionId : '';
  
  let bundleItems = [];
  let bundleSubtotal = 0;
  let productDiscountPercent = 0;

  // Initialize when DOM is ready
  function init() {
    initBundleBuilder();
    initStickyFooter();
    
    // Initialize product discount for first active product
    const firstActiveTab = document.querySelector('.ps5-bundle-product-tab.active');
    if (firstActiveTab) {
      const firstProductDiscount = parseFloat(firstActiveTab.dataset.productDiscount) || 0;
      productDiscountPercent = firstProductDiscount;
      setTimeout(() => updateAddonPrices(firstProductDiscount), 100);
      setTimeout(() => updateBundleSummary(), 100);
      setTimeout(() => updateFreeSectionVisibility(), 100);
    }
  }

  // Handle sticky footer mask and border
  function initStickyFooter() {
    const stickyFooter = document.querySelector('.ps5-bundle-pricing-cart-sticky');
    if (!stickyFooter) return;

    // Use IntersectionObserver to detect when footer reaches bottom
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Check if we're at the bottom of the section
          const section = stickyFooter.closest('[data-ps5-bundle-section-id]');
          if (section) {
            const sectionRect = section.getBoundingClientRect();
            const footerRect = stickyFooter.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            
            // If footer is at or near the bottom of the viewport and section
            const isAtBottom = footerRect.bottom >= windowHeight - 1 || 
                              (sectionRect.bottom <= windowHeight + 10);
            
            if (isAtBottom) {
              stickyFooter.classList.add('at-bottom');
            } else {
              stickyFooter.classList.remove('at-bottom');
            }
          }
        }
      });
    }, {
      threshold: [0, 1],
      rootMargin: '0px'
    });

    observer.observe(stickyFooter);

    // Also check on scroll for more accurate detection
    let scrollTimeout;
    function checkFooterPosition() {
      const section = stickyFooter.closest('[data-ps5-bundle-section-id]');
      if (!section) return;

      const sectionRect = section.getBoundingClientRect();
      const footerRect = stickyFooter.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const scrollY = window.scrollY || window.pageYOffset;
      const sectionBottom = sectionRect.bottom + scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Check if we're at the bottom of the section or document
      const isAtBottom = (sectionBottom <= windowHeight + scrollY + 10) || 
                        (scrollY + windowHeight >= documentHeight - 10);

      if (isAtBottom) {
        stickyFooter.classList.add('at-bottom');
      } else {
        stickyFooter.classList.remove('at-bottom');
      }
    }

    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(checkFooterPosition, 10);
    }, { passive: true });

    // Initial check
    setTimeout(checkFooterPosition, 100);
  }

  // Bundle Builder
  function initBundleBuilder() {
    const bundleBuilder = document.getElementById('ps5-bundle-builder');
    if (!bundleBuilder) return;

    // Initialize bundle items with main product
    const firstActiveTab = document.querySelector('.ps5-bundle-product-tab.active');
    if (firstActiveTab) {
      const variantId = firstActiveTab.dataset.variantId;
      const price = parseInt(firstActiveTab.dataset.productPrice || 0);
      const productTitle = firstActiveTab.dataset.productTitle || firstActiveTab.querySelector('.ps5-tab-label')?.textContent || 'Main Product';
      
      bundleItems = [{
        variantId: variantId,
        price: price,
        name: productTitle,
        type: 'main'
      }];
      bundleSubtotal = price;
    }

    // Product tab switching
    const productTabs = document.querySelectorAll('.ps5-bundle-product-tab');
    productTabs.forEach(tab => {
      tab.addEventListener('click', async function() {
        const productHandle = this.dataset.productHandle;
        const configId = this.dataset.configId;
        await switchBundleProduct(productHandle, configId, this);
      });
    });

    // Addon checkbox handling
    document.addEventListener('change', function(e) {
      if (e.target.classList.contains('ps5-addon-checkbox')) {
        const checkbox = e.target;
        const addon = checkbox.closest('.ps5-bundle-addon');
        if (!addon || addon.classList.contains('hidden')) return;
        
        const addonProductId = addon.dataset.addonProductId;
        const addonName = addon.querySelector('h4')?.textContent || '';
        const isChecked = checkbox.checked;
        
        handleAddonToggle(addon, checkbox, addonProductId, addonName, isChecked);
      }
    });

    // Addon row clickability
    document.addEventListener('click', function(e) {
      const addon = e.target.closest('.ps5-bundle-addon');
      if (!addon || addon.classList.contains('hidden')) return;
      
      // Don't trigger if clicking directly on checkbox
      if (e.target.classList.contains('ps5-addon-checkbox') || e.target.closest('.ps5-addon-checkbox-wrapper')) {
        return;
      }
      
      const checkbox = addon.querySelector('.ps5-addon-checkbox');
      if (checkbox) {
        checkbox.checked = !checkbox.checked;
        const addonProductId = addon.dataset.addonProductId;
        const addonName = addon.querySelector('h4')?.textContent || '';
        handleAddonToggle(addon, checkbox, addonProductId, addonName, checkbox.checked);
      }
    });

    // Bundle form submission
    const bundleForm = document.getElementById('ps5-bundle-form');
    if (bundleForm) {
      bundleForm.addEventListener('submit', async function(e) {
        e.preventDefault();
        await addBundleToCart();
      });
    }

    // Initial update
    updateBundleSummary();
    
    // Hide all free sections initially (will show when addon is selected)
    updateFreeSectionVisibility();
    
    // Initial combo image update
    setTimeout(() => updateComboImage(), 200);
  }

  function handleAddonToggle(addon, checkbox, addonProductId, addonName, isChecked) {
    const oldItem = bundleItems.find(item => item.addonProductId === addonProductId);
    if (oldItem) {
      bundleSubtotal -= oldItem.price;
      bundleItems = bundleItems.filter(item => item.addonProductId !== addonProductId);
    }

    if (isChecked) {
      const price = parseInt(checkbox.dataset.addonPrice || 0);
      const variantId = checkbox.dataset.addonVariantId;
      const productHandle = checkbox.dataset.addonProductHandle || addon.dataset.addonProductHandle;
      
      bundleItems.push({
        variantId: variantId,
        price: price,
        name: addonName,
        type: 'addon',
        addonProductId: addonProductId,
        productHandle: productHandle
      });
      bundleSubtotal += price;
    }
    
    // Update combo image based on selected addons
    updateComboImage();
    
    manageFreeProducts();
    updateFreeSectionVisibility();
    updateBundleSummary();
  }

  // Update main image based on combo image logic
  function updateComboImage() {
    const mainImageEl = document.getElementById('bundle-main-image');
    if (!mainImageEl) return;

    // Get current config ID
    const activeTab = document.querySelector('.ps5-bundle-product-tab.active');
    if (!activeTab) return;
    
    const configId = activeTab.dataset.configId;
    if (!configId) return;

    // Get combo images data for this config
    const comboDataEl = document.querySelector(`.combo-images-data[data-config-id="${configId}"]`);
    if (!comboDataEl) {
      // Fallback to original image if no combo data
      if (mainImageEl.dataset.originalImage) {
        mainImageEl.src = mainImageEl.dataset.originalImage;
      }
      return;
    }

    // Get all checked addons with their indices (1, 2, 4 - skipping 3)
    const checkedAddons = [];
    document.querySelectorAll('.ps5-bundle-addon:not(.hidden) .ps5-addon-checkbox:checked').forEach(checkbox => {
      const addon = checkbox.closest('.ps5-bundle-addon');
      const addonIndex = parseInt(addon.dataset.addonIndex || '0');
      // Only track addons 1, 2, and 4 (skip 3)
      if (addonIndex === 1 || addonIndex === 2 || addonIndex === 4) {
        checkedAddons.push(addonIndex);
      }
    });

    // Sort addons for consistent checking
    checkedAddons.sort((a, b) => a - b);

    // Determine which combo image to show based on combination
    // Priority: check most specific combinations first
    let comboImageUrl = null;
    const has1 = checkedAddons.includes(1);
    const has2 = checkedAddons.includes(2);
    const has4 = checkedAddons.includes(4);

    // Check combinations from most specific to least specific
    // Note: dataset properties convert data-combo-1 to dataset.combo1 (camelCase)
    // Using getAttribute as fallback for compatibility
    if (has1 && has2 && has4) {
      // All three: 2+1+4 or 4+1+2 -> combo_image_6
      comboImageUrl = comboDataEl.dataset.combo6 || comboDataEl.getAttribute('data-combo-6');
    } else if (has2 && has1) {
      // 2 + 1 -> combo_image_5
      comboImageUrl = comboDataEl.dataset.combo5 || comboDataEl.getAttribute('data-combo-5');
    } else if (has4 && has1) {
      // 4 + 1 -> combo_image_7
      comboImageUrl = comboDataEl.dataset.combo7 || comboDataEl.getAttribute('data-combo-7');
    } else if (has4 && has2) {
      // 4 + 2 -> combo_image_3
      comboImageUrl = comboDataEl.dataset.combo3 || comboDataEl.getAttribute('data-combo-3');
    } else if (has1) {
      // Just 1 -> combo_image_1
      comboImageUrl = comboDataEl.dataset.combo1 || comboDataEl.getAttribute('data-combo-1');
    } else if (has2) {
      // Just 2 -> combo_image_2
      comboImageUrl = comboDataEl.dataset.combo2 || comboDataEl.getAttribute('data-combo-2');
    } else if (has4) {
      // Just 4 -> combo_image_4
      comboImageUrl = comboDataEl.dataset.combo4 || comboDataEl.getAttribute('data-combo-4');
    }

    // Update image
    if (!mainImageEl.dataset.originalImage) {
      mainImageEl.dataset.originalImage = mainImageEl.src;
    }

    if (comboImageUrl && comboImageUrl.trim() !== '') {
      mainImageEl.src = comboImageUrl;
    } else if (mainImageEl.dataset.originalImage) {
      // Fallback to original image if no combo image matches
      mainImageEl.src = mainImageEl.dataset.originalImage;
    }
  }

  async function switchBundleProduct(handle, configId, activeTab) {
    try {
      // Update active tab
      document.querySelectorAll('.ps5-bundle-product-tab').forEach(t => t.classList.remove('active'));
      activeTab.classList.add('active');

      // Fetch product data
      const response = await fetch(`/products/${handle}.js`);
      const product = await response.json();

      // Update main image
      const imageEl = document.getElementById('bundle-main-image');
      const variantInput = document.getElementById('bundle-main-variant-id');
      const linkEl = document.getElementById('bundle-main-link');
      if (linkEl) {
        linkEl.href = `/products/${handle}`;
        if (product.title) linkEl.setAttribute('aria-label', product.title);
      }
      
      const manualImage = activeTab.dataset.productImage;
      if (imageEl) {
        if (manualImage && manualImage.trim() !== '') {
          imageEl.src = manualImage;
          imageEl.style.display = '';
          imageEl.dataset.originalImage = manualImage;
        } else if (product.featured_image) {
          imageEl.src = product.featured_image;
          imageEl.style.display = '';
          imageEl.dataset.originalImage = product.featured_image;
        } else if (product.images && product.images.length > 0) {
          imageEl.src = product.images[0];
          imageEl.style.display = '';
          imageEl.dataset.originalImage = product.images[0];
        }
      }
      
      if (variantInput) {
        const tabVariantId = activeTab.dataset.variantId || product.variants[0].id;
        variantInput.value = tabVariantId;
        variantInput.dataset.price = activeTab.dataset.productPrice || product.variants[0].price;
        variantInput.dataset.configId = configId;
        variantInput.dataset.productHandle = handle;
      }

      bundleItems = bundleItems.filter(item => item.type !== 'main');
      bundleItems.unshift({
        variantId: product.variants[0].id,
        price: product.variants[0].price,
        name: product.title,
        type: 'main'
      });

      bundleSubtotal = bundleItems.reduce((sum, item) => sum + item.price, 0);

      loadAddonsForProduct(configId);
      const productDiscount = parseFloat(activeTab.dataset.productDiscount) || 0;
      productDiscountPercent = productDiscount;
      updateAddonPrices(productDiscount);
      loadFreeProductsForProduct(configId);
      
      // Remove all addon and free items when switching products
      bundleItems = bundleItems.filter(item => item.type === 'main');
      bundleSubtotal = bundleItems.reduce((sum, item) => sum + item.price, 0);
      
      // Only add free products if at least 1 addon is selected
      manageFreeProducts();
      updateFreeSectionVisibility();

      // Update combo image after switching products (takes priority over single addon images)
      updateComboImage();
      
      updateBundleSummary();
      setTimeout(() => {
        updateAddonLayout();
        // Update tab discounts after switching products
        const addonCount = bundleItems.filter(item => item.type === 'addon').length;
        updateProductTabDiscounts(addonCount);
      }, 150);
    } catch (error) {
      console.error('Error switching product:', error);
    }
  }

  function updateFreeSectionVisibility() {
    const addonCount = bundleItems.filter(item => item.type === 'addon').length;
    const allFreeSections = document.querySelectorAll('.ps5-bundle-free-section');
    
    let activeTab = document.querySelector('.ps5-bundle-product-tab.active');
    if (!activeTab) {
      activeTab = document.querySelector('.ps5-bundle-tab.active');
    }
    const configId = activeTab ? (activeTab.dataset.configId || activeTab.getAttribute('data-config-id')) : null;
    
    allFreeSections.forEach(section => {
      const sectionConfigId = section.dataset.configId || section.getAttribute('data-config-id');
      
      if (addonCount >= 1 && configId && sectionConfigId === configId) {
        const freeItems = section.querySelectorAll('.ps5-bundle-free-item');
        const hasValidFreeItems = Array.from(freeItems).some(item => {
          const variantId = item.dataset.freeVariantId || item.getAttribute('data-free-variant-id');
          const titleEl = item.querySelector('.ps5-bundle-free-title');
          const title = titleEl ? titleEl.textContent.trim() : '';
          return variantId && variantId.trim() !== '' && title !== '';
        });
        
        if (hasValidFreeItems) {
          section.classList.remove('hidden');
        } else {
          section.classList.add('hidden');
        }
      } else {
        section.classList.add('hidden');
      }
    });
  }

  function loadFreeProductsForProduct(configId) {
    const allFreeSections = document.querySelectorAll('.ps5-bundle-free-section');
    allFreeSections.forEach(section => {
      section.classList.add('hidden');
    });
    updateBundleSummary();
  }
  
  function manageFreeProducts() {
    bundleItems = bundleItems.filter(item => item.type !== 'free');
    
    const addonCount = bundleItems.filter(item => item.type === 'addon').length;
    
    if (addonCount >= 1) {
      let activeTab = document.querySelector('.ps5-bundle-tab.active');
      if (!activeTab) {
        activeTab = document.querySelector('.ps5-bundle-product-tab.active');
      }
      const configId = activeTab ? (activeTab.dataset.configId || activeTab.getAttribute('data-config-id')) : null;
      
      if (configId) {
        const freeSection = document.querySelector(`.ps5-bundle-free-section[data-config-id="${configId}"]`);
        if (freeSection) {
          const freeItems = freeSection.querySelectorAll('.ps5-bundle-free-item');
          
          freeItems.forEach(item => {
            const variantId = item.dataset.freeVariantId || item.getAttribute('data-free-variant-id');
            const productId = item.dataset.freeProductId || item.getAttribute('data-free-product-id');
            
            if (variantId && variantId.trim() !== '') {
              const titleEl = item.querySelector('.ps5-bundle-free-title');
              const title = titleEl ? titleEl.textContent.trim() : 'Free Item';
              
              if (title && title !== '') {
                addFreeProductToBundle(variantId, title, productId);
              }
            }
          });
        }
      }
    }
  }
  
  function addFreeProductToBundle(variantId, name, productId) {
    bundleItems = bundleItems.filter(item => !(item.type === 'free' && item.productId === productId));
    
    bundleItems.push({
      variantId: variantId,
      price: 0,
      name: name,
      type: 'free',
      productId: productId || ''
    });
  }

  async function loadAddonsForProduct(configId) {
    const container = document.getElementById('bundle-addons-container');
    if (!container) return;

    const allAddons = document.querySelectorAll('.ps5-bundle-addon');
    allAddons.forEach(addon => {
      const addonConfigId = addon.dataset.configId;
      if (addonConfigId === configId) {
        addon.classList.remove('hidden');
        const checkbox = addon.querySelector('.ps5-addon-checkbox');
        if (checkbox) checkbox.checked = false;
      } else {
        addon.classList.add('hidden');
      }
    });
    
    updateAddonLayout();
    
    // Update combo image after loading addons (takes priority over single addon images)
    updateComboImage();
  }

  function updateAddonPrices(discountPercent) {
    const addonPrices = document.querySelectorAll('.addon-price');
    addonPrices.forEach(priceEl => {
      const basePrice = parseInt(priceEl.dataset.price || 0);
      const comparePrice = parseInt(priceEl.dataset.comparePrice || 0);
      const productDiscount = parseFloat(priceEl.dataset.productDiscount || 0);
      
      if (basePrice > 0) {
        let finalPrice = basePrice;
        let displayComparePrice = comparePrice;
        
        if (productDiscount > 0) {
          const discountMultiplier = (100 - productDiscount) / 100;
          finalPrice = Math.round(basePrice * discountMultiplier);
          displayComparePrice = basePrice;
        }
        
        const comparePriceEl = priceEl.querySelector('.addon-compare-price');
        const currentPriceEl = priceEl.querySelector('.addon-current-price');
        
        if (displayComparePrice > finalPrice && comparePriceEl) {
          comparePriceEl.textContent = formatPrice(displayComparePrice);
          comparePriceEl.style.display = '';
        } else if (comparePriceEl) {
          comparePriceEl.style.display = 'none';
        }
        
        if (currentPriceEl) {
          currentPriceEl.textContent = '+' + formatPrice(finalPrice);
        }
      }
    });
  }

  function updateAddonLayout() {
    // This function can be used for any layout updates needed
  }

  async function findAvailableVariant(productHandle, currentVariantId) {
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) return null;
      const product = await response.json();
      
      const availableVariant = product.variants?.find(v => 
        v.available && v.id.toString() !== currentVariantId.toString()
      );
      if (availableVariant) {
        return availableVariant.id.toString();
      }
      
      const currentVariant = product.variants?.find(v => v.id.toString() === currentVariantId.toString());
      if (currentVariant && currentVariant.available) {
        return currentVariantId.toString();
      }
      
      return null;
    } catch (error) {
      console.warn('Could not find available variant:', error);
      return null;
    }
  }

  async function addBundleToCart() {
    if (bundleItems.length === 0) {
      alert('Please select at least the main product');
      return;
    }

    const addToCartButton = document.querySelector('.ps5-bundle-cart-button');
    if (addToCartButton) {
      addToCartButton.setAttribute('aria-busy', 'true');
      addToCartButton.disabled = true;
    }

    console.log('Adding bundle to cart. Bundle items:', bundleItems);
    
    const validItems = bundleItems.filter(item => {
      const isValid = item.variantId && item.variantId.toString().trim() !== '';
      if (!isValid) {
        console.warn('Skipping item with invalid variant ID:', item);
      }
      return isValid;
    });

    if (validItems.length === 0) {
      alert('No valid items to add to cart');
      if (addToCartButton) {
        addToCartButton.removeAttribute('aria-busy');
        addToCartButton.disabled = false;
      }
      return;
    }

    let currentCart = null;
    try {
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        currentCart = await cartResponse.json();
      }
    } catch (error) {
      console.warn('Could not fetch current cart:', error);
    }

    const freeItems = validItems.filter(item => item.type === 'free');
    const regularItems = validItems.filter(item => item.type !== 'free');

    const itemsToAdd = [];
    const unavailableItems = [];
    
    for (const item of regularItems) {
      try {
        let productHandle = item.productHandle;
        
        if (!productHandle) {
          if (item.addonProductId) {
            const addonCheckbox = document.querySelector(`[data-addon-variant-id="${item.variantId}"]`);
            if (addonCheckbox) {
              productHandle = addonCheckbox.dataset.addonProductHandle || 
                             addonCheckbox.closest('.ps5-bundle-addon')?.dataset.addonProductHandle;
            }
          } else if (item.type === 'main') {
            const activeTab = document.querySelector('.ps5-bundle-product-tab.active');
            if (activeTab) {
              productHandle = activeTab.dataset.productHandle || activeTab.dataset.handle;
            }
          }
        }
        
        if (productHandle) {
          const availableVariantId = await findAvailableVariant(productHandle, item.variantId);
          if (availableVariantId) {
            itemsToAdd.push({
              id: availableVariantId,
              quantity: 1,
              originalItem: item
            });
            if (availableVariantId !== item.variantId.toString()) {
              item.variantId = availableVariantId;
              console.log(`Switched variant for ${item.name} from ${item.variantId} to ${availableVariantId}`);
            }
          } else {
            itemsToAdd.push({
              id: item.variantId,
              quantity: 1,
              originalItem: item
            });
          }
        } else {
          itemsToAdd.push({
            id: item.variantId,
            quantity: 1,
            originalItem: item
          });
        }
      } catch (error) {
        console.warn(`Error processing item ${item.name}:`, error);
        unavailableItems.push(item.name);
      }
    }

    const freeItemUpdates = {};
    if (currentCart && currentCart.items) {
      for (const freeItem of freeItems) {
        const existingCartItem = currentCart.items.find(cartItem => 
          cartItem.variant_id === parseInt(freeItem.variantId)
        );
        if (existingCartItem) {
          freeItemUpdates[existingCartItem.key] = 1;
        } else {
          itemsToAdd.push({
            id: freeItem.variantId,
            quantity: 1,
            originalItem: freeItem
          });
        }
      }
    } else {
      for (const freeItem of freeItems) {
        itemsToAdd.push({
          id: freeItem.variantId,
          quantity: 1,
          originalItem: freeItem
        });
      }
    }
    
    console.log('Cart items to add:', itemsToAdd);
    console.log('Free item updates:', freeItemUpdates);
    if (unavailableItems.length > 0) {
      console.warn('Unavailable items:', unavailableItems);
    }

    if (itemsToAdd.length === 0 && Object.keys(freeItemUpdates).length === 0) {
      alert('All selected items are currently sold out. Please try again later.');
      if (addToCartButton) {
        addToCartButton.removeAttribute('aria-busy');
        addToCartButton.disabled = false;
      }
      return;
    }

    try {
      let addResponse = null;
      const failedItems = [];
      
      if (itemsToAdd.length > 0) {
        for (const item of itemsToAdd) {
          try {
            const itemResponse = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [item] })
            });

            if (!itemResponse.ok) {
              const errorData = await itemResponse.json();
              const errorMessage = errorData.description || 'Failed to add item';
              
              if (errorMessage.toLowerCase().includes('sold out')) {
                const originalItem = item.originalItem || 
                                   regularItems.find(ri => ri.variantId.toString() === item.id.toString()) ||
                                   freeItems.find(fi => fi.variantId.toString() === item.id.toString());
                
                let productHandle = originalItem?.productHandle;
                
                if (!productHandle && originalItem) {
                  if (originalItem.addonProductId) {
                    const addonCheckbox = document.querySelector(`[data-addon-variant-id="${item.id}"]`);
                    if (addonCheckbox) {
                      productHandle = addonCheckbox.dataset.addonProductHandle || 
                                     addonCheckbox.closest('.ps5-bundle-addon')?.dataset.addonProductHandle;
                    }
                  } else if (originalItem.type === 'main') {
                    const activeTab = document.querySelector('.ps5-bundle-product-tab.active');
                    if (activeTab) {
                      productHandle = activeTab.dataset.productHandle || activeTab.dataset.handle;
                    }
                  }
                }
                
                if (productHandle) {
                  const altVariantId = await findAvailableVariant(productHandle, item.id);
                  if (altVariantId && altVariantId !== item.id.toString()) {
                    const altResponse = await fetch('/cart/add.js', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ items: [{ id: altVariantId, quantity: 1 }] })
                    });
                    if (altResponse.ok) {
                      console.log(`Successfully added alternative variant ${altVariantId} for ${originalItem?.name || 'item'}`);
                      if (originalItem) {
                        originalItem.variantId = altVariantId;
                      }
                      continue;
                    }
                  }
                }
                
                failedItems.push(originalItem?.name || `Variant ${item.id}`);
              } else {
                failedItems.push(item.originalItem?.name || `Item ${item.id}`);
              }
            } else {
              if (!addResponse) {
                addResponse = { ok: true };
              }
            }
          } catch (error) {
            console.error(`Error adding item ${item.id}:`, error);
            failedItems.push(`Item ${item.id}`);
          }
        }
      }

      if (Object.keys(freeItemUpdates).length > 0) {
        const updateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: freeItemUpdates })
        });

        if (!updateResponse.ok) {
          console.warn('Could not update free item quantities:', await updateResponse.json());
        }
      }

      // Check if we successfully added any items
      const hasSuccessfulAdd = addResponse?.ok || Object.keys(freeItemUpdates).length > 0;
      
      if (hasSuccessfulAdd) {
        try {
          const cartJson = await (await fetch('/cart.js', { 
            headers: { 'Content-Type': 'application/json' }
          })).json();
        
          // Update cart count - use manual update to avoid section errors
          document.querySelectorAll('cart-count').forEach(el => {
            if (cartJson.item_count !== undefined) {
              el.innerText = cartJson.item_count;
              el.hidden = cartJson.item_count === 0;
            }
          });
          
          // Try to publish cart update event, but don't fail if it errors
          if (window.theme && window.theme.pubsub && window.theme.pubsub.PUB_SUB_EVENTS) {
            try {
              window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                cart: cartJson
              });
            } catch (pubsubError) {
              // Silently fail - we already updated cart count manually
              console.warn('Could not publish cart update event:', pubsubError);
            }
          }
        } catch (cartFetchError) {
          console.warn('Could not fetch updated cart:', cartFetchError);
        }
        
        const cartDrawerEl = document.querySelector('#CartDrawer');
        if (cartDrawerEl) {
          const cartDrawer = window.theme?.cartDrawer || window.CartDrawer;
          if (cartDrawer) {
            cartDrawer.show();
          }
        }
        
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        
        if (failedItems.length > 0) {
          setTimeout(() => {
            alert(`Some items could not be added to your cart:\n${failedItems.join('\n')}\n\nPlease check your cart and try adding them separately.`);
          }, 500);
        }
      } else if (failedItems.length > 0) {
        alert(`Could not add the following items to your cart:\n${failedItems.join('\n')}\n\nThey may be sold out. Please try again later.`);
      } else {
        alert('Could not add items to cart. Please try again.');
      }
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      alert('Error adding bundle to cart. Please try again.');
    } finally {
      if (addToCartButton) {
        addToCartButton.removeAttribute('aria-busy');
        addToCartButton.disabled = false;
      }
    }
  }

  function updateBundleSummary() {
    // Count addons
    const addonCount = bundleItems.filter(item => item.type === 'addon').length;
    
    // Calculate discount - only show if at least 1 addon is selected
    let discountAmount = 0;
    let discountPercentDisplay = 0;
    
    if (productDiscountPercent > 0 && addonCount >= 1) {
      discountAmount = Math.round(bundleSubtotal * productDiscountPercent / 100);
      discountPercentDisplay = Math.round(productDiscountPercent);
    }
    const finalTotal = bundleSubtotal - discountAmount;

    // Update pricing display
    const originalPriceEl = document.getElementById('bundle-original-price-display');
    const originalPriceValue = document.getElementById('bundle-original-price-value');
    const finalPriceEl = document.getElementById('bundle-final-price-display');
    const finalPriceValue = document.getElementById('bundle-final-price-value');
    const discountBadgeTop = document.getElementById('bundle-discount-badge-top');
    const discountPercentEl = document.getElementById('bundle-discount-percent');

    if (finalPriceValue) finalPriceValue.textContent = formatPrice(finalTotal);

    if (discountAmount > 0 && addonCount >= 1) {
      if (originalPriceEl) originalPriceEl.style.display = 'block';
      if (originalPriceValue) originalPriceValue.textContent = formatPrice(bundleSubtotal);
      if (discountBadgeTop) discountBadgeTop.style.display = 'block';
      if (discountPercentEl) discountPercentEl.textContent = discountPercentDisplay + '% OFF';
    } else {
      if (originalPriceEl) originalPriceEl.style.display = 'none';
      if (discountBadgeTop) discountBadgeTop.style.display = 'none';
    }
    
    // Update product tabs to show/hide discount based on addon count
    updateProductTabDiscounts(addonCount);
  }
  
  function updateProductTabDiscounts(addonCount) {
    const productTabs = document.querySelectorAll('.ps5-bundle-product-tab');
    const activeTab = document.querySelector('.ps5-bundle-product-tab.active');
    
    productTabs.forEach(tab => {
      const discount = parseFloat(tab.dataset.productDiscount) || 0;
      const comparePriceEl = tab.querySelector('.ps5-tab-compare-price');
      const isActive = tab === activeTab;
      
      // Only show discount on the active tab if at least 1 addon is selected
      if (discount > 0 && addonCount >= 1 && isActive) {
        // Show discount - calculate discounted price
        const originalPrice = parseFloat(tab.dataset.productPrice) || 0;
        const discountMultiplier = (100 - discount) / 100;
        const discountedPrice = Math.round(originalPrice * discountMultiplier);
        const currentPriceEl = tab.querySelector('.ps5-tab-current-price');
        
        if (currentPriceEl) {
          currentPriceEl.textContent = formatPrice(discountedPrice);
        }
        if (comparePriceEl) {
          comparePriceEl.textContent = formatPrice(originalPrice);
          comparePriceEl.style.display = '';
        }
      } else {
        // Hide discount - show original price
        const originalPrice = parseFloat(tab.dataset.productPrice) || 0;
        const currentPriceEl = tab.querySelector('.ps5-tab-current-price');
        
        if (currentPriceEl) {
          currentPriceEl.textContent = formatPrice(originalPrice);
        }
        if (comparePriceEl) {
          comparePriceEl.style.display = 'none';
        }
      }
    });
  }

  function formatPrice(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


